export function getListingImageBaseUrl() {
  const isDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  return isDev ? 'http://127.0.0.1:8080' : '';
}

export function resolveListingImageUrl(raw, baseUrl = getListingImageBaseUrl()) {
  if (!raw || typeof raw !== 'string') return null;
  if (raw.startsWith('http')) return raw;
  return `${baseUrl}${raw}`;
}

/**
 * flea-image-resize Worker on flea-marketplace.com (verified 2026-09-02).
 * Do not use images.flea-marketplace.com — that host NXDOMAINs.
 */
const IMAGE_CDN_HOST = (process.env.REACT_APP_IMAGE_CDN_HOST || 'flea-marketplace.com')
  .trim()
  .replace(/^https?:\/\//, '')
  .replace(/\/+$/, '');

const WIDTH_BUCKETS = [160, 320, 480, 640, 960, 1280, 1600];
const RESIZABLE_HOSTS = new Set(['pub-3076d9dbe3fb4c56b2baab8d158c7d56.r2.dev']);

function snapWidth(pixelWidth) {
  return WIDTH_BUCKETS.find((w) => w >= pixelWidth) ?? WIDTH_BUCKETS[WIDTH_BUCKETS.length - 1];
}

function canResizeRemoteUrl(url) {
  if (!url || !url.startsWith('http')) return false;
  if (url.includes('/img/width=') || url.includes('/cdn-cgi/image/')) return false;
  try {
    return RESIZABLE_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
}

/**
 * Request a resized derivative from the Worker:
 *   /img/width=W,quality=75,format=auto,fit=scale-down/<absolute-original-url>
 */
export function sizedImageUrl(url, cssWidth) {
  if (!url) return null;
  if (!IMAGE_CDN_HOST || !cssWidth || cssWidth <= 0) return url;
  if (!canResizeRemoteUrl(url)) return url;

  let source = url;
  try {
    const parsed = new URL(url);
    parsed.search = '';
    parsed.hash = '';
    source = parsed.toString();
  } catch {
    return url;
  }

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const width = snapWidth(Math.ceil(cssWidth * Math.min(dpr, 2)));
  return `https://${IMAGE_CDN_HOST}/img/width=${width},quality=75,format=auto,fit=scale-down/${source}`;
}

function inferMediaType(url, explicitType) {
  if (explicitType === 'video' || explicitType === 'image') return explicitType;
  const lower = (url || '').toLowerCase().split('?')[0];
  if (/\.(mp4|mov|webm|m4v)$/.test(lower)) return 'video';
  return 'image';
}

/** Collect listing media (photos + videos) in display order. */
export function collectListingMediaItems(item, baseUrl = getListingImageBaseUrl()) {
  if (!item) return [];

  const items = [];
  const seen = new Set();

  const add = (raw, mediaType) => {
    const url = resolveListingImageUrl(raw, baseUrl);
    if (!url || seen.has(url)) return;
    seen.add(url);
    items.push({ url, mediaType: inferMediaType(url, mediaType) });
  };

  if (Array.isArray(item.images) && item.images.length) {
    item.images.forEach((entry) => {
      if (typeof entry === 'string') {
        add(entry);
      } else if (entry?.image) {
        add(entry.image, entry.media_type || entry.mediaType);
      }
    });
    return items;
  }

  if (item.primary_image) add(item.primary_image);
  if (item.image_url) add(item.image_url);
  return items;
}

/** Collect unique listing photo URLs (skips videos) for cards/thumbnails. */
export function collectListingImageUrls(item, baseUrl = getListingImageBaseUrl()) {
  return collectListingMediaItems(item, baseUrl)
    .filter((m) => m.mediaType !== 'video')
    .map((m) => m.url);
}
